import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  User,
  Mail,
  Phone,
  Trophy,
  Calendar,
  ArrowLeft,
  RefreshCw,
  Filter,
  Search,
  MapPin,
  ChevronDown
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const AdminPendingRegistrations = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, unpaid
  const [searchTerm, setSearchTerm] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    loadRegistrations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const handleDropdownClick = (e, dropdown) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('https://api.fsgu.co.za/tournament-registrations.php?status=pending', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to load registrations');
      }

      setRegistrations(data.data || []);
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError(err.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (registrationId) => {
    if (!window.confirm('Are you sure you want to approve this registration?')) {
      return;
    }

    try {
      setProcessingId(registrationId);

      const response = await fetch('https://api.fsgu.co.za/update-registration.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'status',
          registration_id: registrationId,
          status: 'confirmed'
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to approve registration');
      }

      loadRegistrations();
      alert('Registration approved successfully!');
    } catch (err) {
      console.error('Error approving registration:', err);
      alert(err.message || 'Failed to approve registration');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (registrationId) => {
    const reason = window.prompt('Please enter a reason for rejection (optional):');
    if (reason === null) return;

    try {
      setProcessingId(registrationId);

      const response = await fetch('https://api.fsgu.co.za/update-registration.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'status',
          registration_id: registrationId,
          status: 'rejected',
          rejection_reason: reason
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to reject registration');
      }

      loadRegistrations();
      alert('Registration rejected successfully.');
    } catch (err) {
      console.error('Error rejecting registration:', err);
      alert(err.message || 'Failed to reject registration');
    } finally {
      setProcessingId(null);
    }
  };

  const handleMarkPaid = async (registrationId) => {
    if (!window.confirm('Mark this registration as paid?')) {
      return;
    }

    try {
      setProcessingId(registrationId);

      const response = await fetch('https://api.fsgu.co.za/update-registration.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'payment',
          registration_id: registrationId,
          payment_status: 'paid',
          payment_date: new Date().toISOString().split('T')[0]
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to update payment status');
      }

      loadRegistrations();
      alert('Payment status updated successfully!');
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert(err.message || 'Failed to update payment status');
    } finally {
      setProcessingId(null);
    }
  };

  // Filter and search registrations
  const filteredRegistrations = registrations.filter(reg => {
    // Status filter
    if (filterStatus === 'pending' && reg.status !== 'pending') return false;
    if (filterStatus === 'unpaid' && reg.payment_status !== 'pending') return false;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        reg.player_name?.toLowerCase().includes(search) ||
        reg.player_email?.toLowerCase().includes(search) ||
        reg.tournament_name?.toLowerCase().includes(search) ||
        reg.registration_number?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isDarkMode
              ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400'
              : 'bg-fsgu-gold-100 text-fsgu-gold-700'
          }`}>
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'confirmed':
        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isDarkMode
              ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
              : 'bg-fsgu-green-100 text-fsgu-green-700'
          }`}>
            <CheckCircle className="w-3 h-3 mr-1" />
            Confirmed
          </span>
        );
      case 'rejected':
        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isDarkMode
              ? 'bg-fsgu-red-900/30 text-fsgu-red-400'
              : 'bg-fsgu-red-100 text-fsgu-red-700'
          }`}>
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const getPaymentBadge = (paymentStatus) => {
    switch (paymentStatus) {
      case 'pending':
        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isDarkMode
              ? 'bg-fsgu-orange-900/30 text-fsgu-orange-400'
              : 'bg-fsgu-orange-100 text-fsgu-orange-700'
          }`}>
            <CreditCard className="w-3 h-3 mr-1" />
            Unpaid
          </span>
        );
      case 'paid':
        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isDarkMode
              ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
              : 'bg-fsgu-green-100 text-fsgu-green-700'
          }`}>
            <CheckCircle className="w-3 h-3 mr-1" />
            Paid
          </span>
        );
      case 'refunded':
        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isDarkMode
              ? 'bg-fsgu-blue-900/30 text-fsgu-blue-400'
              : 'bg-fsgu-blue-100 text-fsgu-blue-700'
          }`}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Refunded
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={`mt-4 ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            Loading pending registrations...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Page Header */}
      <section className={`py-8 border-b ${
        isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
      }`}>
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <button
                onClick={() => navigate('/admin/dashboard')}
                className={`p-2.5 rounded-lg transition-all ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 hover:bg-fsgu-gray-700 text-fsgu-silver-100 border border-fsgu-gray-700'
                    : 'bg-fsgu-white hover:bg-fsgu-silver-50 text-fsgu-silver-900 border border-fsgu-silver-300 shadow-sm'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              {/* Title */}
              <div>
                <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  Pending Registrations
                </h1>
                <p className={`text-sm sm:text-base mt-1 ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                }`}>
                  Review and manage tournament registrations
                </p>
              </div>
            </div>

            <button
              onClick={loadRegistrations}
              disabled={loading}
              className="btn btn-outline inline-flex items-center self-start sm:self-auto"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <section className="py-6">
          <div className="container-custom">
            <div className={`p-4 rounded-xl border flex items-start ${
              isDarkMode
                ? 'bg-fsgu-red-900/20 border-fsgu-red-500/30'
                : 'bg-fsgu-red-50 border-fsgu-red-200'
            }`}>
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 mr-3 ${
                isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-500'
              }`} />
              <div>
                <p className={`text-sm font-medium ${
                  isDarkMode ? 'text-fsgu-red-300' : 'text-fsgu-red-700'
                }`}>
                  Error loading registrations
                </p>
                <p className={`text-sm mt-1 ${
                  isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-600'
                }`}>
                  {error}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stats Cards */}
      <section className="py-6">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Total Pending */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    Total Pending
                  </p>
                  <p className={`text-3xl font-bold ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    {registrations.length}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${
                  isDarkMode ? 'bg-fsgu-gold-500/20' : 'bg-fsgu-gold-500/10'
                }`}>
                  <Clock className={`w-6 h-6 ${
                    isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
                  }`} />
                </div>
              </div>
            </div>

            {/* Awaiting Payment */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    Awaiting Payment
                  </p>
                  <p className={`text-3xl font-bold ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    {registrations.filter(r => r.payment_status === 'pending').length}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${
                  isDarkMode ? 'bg-fsgu-orange-500/20' : 'bg-fsgu-orange-500/10'
                }`}>
                  <CreditCard className={`w-6 h-6 ${
                    isDarkMode ? 'text-fsgu-orange-400' : 'text-fsgu-orange-500'
                  }`} />
                </div>
              </div>
            </div>

            {/* Filtered Results */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    Filtered Results
                  </p>
                  <p className={`text-3xl font-bold ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    {filteredRegistrations.length}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${
                  isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                }`}>
                  <Filter className={`w-6 h-6 ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                  }`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-6">
        <div className="container-custom">
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className={`absolute left-3 top-3 w-5 h-5 pointer-events-none ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                }`} />
                <input
                  type="text"
                  placeholder="Search registrations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg border transition-colors ${
                    isDarkMode
                      ? 'bg-fsgu-gray-900 border-fsgu-gray-600 text-fsgu-silver-100 placeholder-fsgu-silver-500 focus:border-fsgu-green-500'
                      : 'bg-fsgu-white border-fsgu-silver-300 text-fsgu-silver-900 placeholder-fsgu-silver-400 focus:border-fsgu-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-fsgu-green-500/20`}
                />
              </div>

              {/* Status Filter Dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => handleDropdownClick(e, 'status')}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
                    openDropdown === 'status'
                      ? isDarkMode
                        ? 'border-fsgu-green-500 bg-fsgu-green-500/10'
                        : 'border-fsgu-green-500 bg-fsgu-green-500/10'
                      : isDarkMode
                      ? 'bg-fsgu-gray-900 border-fsgu-gray-600 hover:border-fsgu-green-500/50'
                      : 'bg-fsgu-white border-fsgu-silver-300 hover:border-fsgu-green-500/50'
                  } focus:outline-none focus:ring-2 focus:ring-fsgu-green-500/20`}
                >
                  <span className={isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'}>
                    {filterStatus === 'all' && 'All Registrations'}
                    {filterStatus === 'pending' && 'Pending Approval'}
                    {filterStatus === 'unpaid' && 'Unpaid'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${
                    openDropdown === 'status' ? 'rotate-180' : ''
                  } ${isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'}`} />
                </button>

                {/* Dropdown Menu */}
                {openDropdown === 'status' && (
                  <div className={`absolute top-full left-0 right-0 mt-2 rounded-lg shadow-lg border overflow-hidden z-50 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                      : 'bg-fsgu-white border-fsgu-silver-200'
                  }`}>
                    {[
                      { value: 'all', label: 'All Registrations' },
                      { value: 'pending', label: 'Pending Approval' },
                      { value: 'unpaid', label: 'Unpaid' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setFilterStatus(option.value);
                          setOpenDropdown(null);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                          filterStatus === option.value
                            ? isDarkMode
                              ? 'text-fsgu-green-400 bg-fsgu-green-500/10'
                              : 'text-fsgu-green-500 bg-fsgu-green-500/10'
                            : isDarkMode
                            ? 'text-fsgu-silver-300 hover:text-fsgu-green-300 hover:bg-fsgu-gray-700'
                            : 'text-fsgu-silver-600 hover:text-fsgu-green-600 hover:bg-fsgu-silver-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Empty State */}
      {!loading && filteredRegistrations.length === 0 && (
        <section className="py-12">
          <div className="container-custom">
            <div className={`py-20 rounded-xl border text-center ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              <Trophy className={`w-16 h-16 mx-auto mb-4 ${
                isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
              }`} />
              <h3 className={`text-xl font-semibold mb-2 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                No registrations found
              </h3>
              <p className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
              }`}>
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your filters or search terms'
                  : 'All registrations have been processed'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Registrations Grid */}
      {!loading && filteredRegistrations.length > 0 && (
        <section className="py-6 pb-12">
          <div className="container-custom">
            <div className="space-y-4">
              {filteredRegistrations.map((registration) => (
                <div
                  key={registration.id}
                  className={`p-6 rounded-xl border transition-all duration-300 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50'
                      : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-green-500/50 shadow-sm hover:shadow-md'
                  }`}
                >
                  {/* Header Row */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className={`text-xl font-bold ${
                          isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                        }`}>
                          {registration.player_name}
                        </h3>
                        {getStatusBadge(registration.status)}
                        {getPaymentBadge(registration.payment_status)}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className={`font-mono ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          #{registration.registration_number}
                        </span>
                        <span className={isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'}>
                          •
                        </span>
                        <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                          {new Date(registration.created_at).toLocaleDateString('en-ZA', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                      {registration.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(registration.id)}
                            disabled={processingId === registration.id}
                            className="btn btn-primary inline-flex items-center"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {processingId === registration.id ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleReject(registration.id)}
                            disabled={processingId === registration.id}
                            className={`inline-flex items-center px-4 py-2.5 rounded-lg font-medium transition-all ${
                              isDarkMode
                                ? 'bg-fsgu-red-900/20 text-fsgu-red-400 hover:bg-fsgu-red-900/30 border border-fsgu-red-500/30'
                                : 'bg-fsgu-red-50 text-fsgu-red-600 hover:bg-fsgu-red-100 border border-fsgu-red-200'
                            }`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </button>
                        </>
                      )}
                      
                      {registration.payment_status === 'pending' && (
                        <button
                          onClick={() => handleMarkPaid(registration.id)}
                          disabled={processingId === registration.id}
                          className="btn btn-outline inline-flex items-center"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Tournament Info Banner */}
                  <div className={`p-4 rounded-lg mb-6 ${
                    isDarkMode
                      ? 'bg-fsgu-green-500/10 border border-fsgu-green-500/20'
                      : 'bg-fsgu-green-50 border border-fsgu-green-200'
                  }`}>
                    <div className="flex items-center">
                      <Trophy className={`w-5 h-5 mr-3 flex-shrink-0 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                      }`} />
                      <div>
                        <p className={`text-xs mb-0.5 ${
                          isDarkMode ? 'text-fsgu-green-300/70' : 'text-fsgu-green-600/70'
                        }`}>
                          Tournament
                        </p>
                        <p className={`font-semibold ${
                          isDarkMode ? 'text-fsgu-green-300' : 'text-fsgu-green-700'
                        }`}>
                          {registration.tournament_name}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Player Details Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Email */}
                    <div className="flex items-start space-x-3">
                      <Mail className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-500'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs mb-0.5 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                        }`}>
                          Email
                        </p>
                        <p className={`text-sm truncate ${
                          isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                        }`}>
                          {registration.player_email}
                        </p>
                      </div>
                    </div>

                    {/* Phone */}
                    {registration.player_phone && (
                      <div className="flex items-start space-x-3">
                        <Phone className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                        }`} />
                        <div>
                          <p className={`text-xs mb-0.5 ${
                            isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                          }`}>
                            Phone
                          </p>
                          <p className={`text-sm ${
                            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                          }`}>
                            {registration.player_phone}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Club */}
                    {registration.player_club && (
                      <div className="flex items-start space-x-3">
                        <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
                        }`} />
                        <div>
                          <p className={`text-xs mb-0.5 ${
                            isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                          }`}>
                            Golf Club
                          </p>
                          <p className={`text-sm ${
                            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                          }`}>
                            {registration.player_club}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Handicap */}
                    {registration.player_handicap && (
                      <div className="flex items-start space-x-3">
                        <Trophy className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
                        }`} />
                        <div>
                          <p className={`text-xs mb-0.5 ${
                            isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                          }`}>
                            Handicap
                          </p>
                          <p className={`text-sm ${
                            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                          }`}>
                            {registration.player_handicap}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Gender */}
                    {registration.player_gender && (
                      <div className="flex items-start space-x-3">
                        <User className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
                        }`} />
                        <div>
                          <p className={`text-xs mb-0.5 ${
                            isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                          }`}>
                            Gender
                          </p>
                          <p className={`text-sm ${
                            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                          }`}>
                            {registration.player_gender}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Age */}
                    {registration.player_age && (
                      <div className="flex items-start space-x-3">
                        <Calendar className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
                        }`} />
                        <div>
                          <p className={`text-xs mb-0.5 ${
                            isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                          }`}>
                            Age
                          </p>
                          <p className={`text-sm ${
                            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                          }`}>
                            {registration.player_age}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Special Requirements */}
                  {registration.special_requirements && (
                    <div className={`mt-6 p-4 rounded-lg ${
                      isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-50'
                    }`}>
                      <p className={`text-xs font-medium mb-2 uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Special Requirements
                      </p>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        {registration.special_requirements}
                      </p>
                    </div>
                  )}

                  {/* Emergency Contact */}
                  {(registration.emergency_contact_name || registration.emergency_contact_phone) && (
                    <div className={`mt-4 pt-4 border-t ${
                      isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                    }`}>
                      <p className={`text-xs font-medium mb-2 uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        Emergency Contact
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        {registration.emergency_contact_name && (
                          <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                            {registration.emergency_contact_name}
                          </span>
                        )}
                        {registration.emergency_contact_phone && (
                          <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                            {registration.emergency_contact_phone}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminPendingRegistrations;